import {
    createContext,
    forwardRef,
    HTMLAttributes,
    RefObject,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {mergeRefs} from 'utils';
import {z} from 'zod';

/**
 * Context for providing ScrollView functionality
 * Contains the IntersectionObserver instance
 */
const _context = createContext<{
    observer: IntersectionObserver | null;
} | null>(null);

/**
 * Custom event name for scroll view intersection events
 */
const EVENT_NAME = 'scroll-in-view';

/**
 * ScrollViewProvider Component
 *
 * Provides an IntersectionObserver context for tracking when elements are visible
 * within a scrollable container.
 *
 * @param options - Configuration options for the IntersectionObserver
 * @param options.rootMargin - Margin around the root element (CSS-like format)
 * @param options.threshold - Visibility threshold (0.0 to 1.0) to trigger intersection
 * @param props - HTML div element attributes passed to the container
 * @param children - React children to be rendered inside the scrollable container
 * @returns A React component that provides scroll visibility tracking
 */
export const ScrollViewProvider = forwardRef<
    HTMLDivElement,
    {
        options?: {
            rootMargin?: string;
            threshold?: number;
        };
    } & HTMLAttributes<HTMLDivElement>
>(
    (
        {
            children,
            options = {
                threshold: 0.0,
            },
            ...props
        },
        ref,
    ) => {
        const innerRef = useRef<HTMLDivElement>(null);
        const [observer, setObserver] = useState<IntersectionObserver | null>(
            null,
        );

        useEffect(() => {
            const element = innerRef.current;
            if (!element) return;
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const event = new CustomEvent(EVENT_NAME, {
                            detail: entry,
                        });
                        entry.target.dispatchEvent(event);
                    });
                },
                {
                    ...options,
                    root: element,
                },
            );
            setObserver(observer);
            return () => {
                observer.disconnect();
            };
        }, [innerRef.current]);

        const value = useMemo(
            () => ({
                observer,
            }),
            [observer],
        );

        return (
            <_context.Provider value={value}>
                <div ref={mergeRefs(innerRef, ref)} {...props}>
                    {children}
                </div>
            </_context.Provider>
        );
    },
);

/**
 * Hook to access the ScrollViewProvider context
 *
 * @returns The ScrollView context containing the IntersectionObserver
 * @throws Error if used outside of a ScrollViewProvider
 */
export const useScrollViewContext = () => {
    const context = useContext(_context);
    if (!context) {
        throw new Error(
            'useScrollView must be used within a ScrollViewProvider',
        );
    }
    return context;
};

/**
 * Hook to track element visibility within a ScrollViewProvider
 *
 * This hook allows tracking whether an element is currently visible
 * within the scrollable container defined by ScrollViewProvider.
 *
 * @param ref - React ref to the HTML element to be observed
 * @returns Object containing isIntersecting state (true when element is visible)
 */
export const useScrollViewVisibility = (ref: RefObject<HTMLElement>) => {
    const methods = useScrollViewContext();

    const [data, setData] = useState<{
        isIntersecting: boolean;
    }>({
        isIntersecting: false,
    });

    // Register the element with the IntersectionObserver
    useEffect(() => {
        const element = ref.current;
        const observer = methods.observer;
        if (!element || !observer) return;
        observer.observe(element);
        return () => {
            observer.unobserve(element);
        };
    }, [methods, ref.current]);

    // Listen for intersection events and update state
    useEffect(() => {
        const element = ref.current;
        if (!element) return;
        const handleInView = (event: Event) => {
            const _data = z
                .object({
                    detail: z.object({
                        isIntersecting: z.boolean(),
                    }),
                })
                .parse(event);
            setData(_data.detail);
        };
        element.addEventListener(EVENT_NAME, handleInView);
        return () => {
            element.removeEventListener(EVENT_NAME, handleInView);
        };
    }, [ref.current]);

    return data;
};
